/**
 * Exercise picker.
 *
 * Search is client-side against the Dexie cache, so it works offline and
 * responds on every keystroke without a round trip. The list is ~93 canonical
 * exercises plus whatever the user has added, which is far too small to justify
 * anything cleverer than a filter.
 *
 * Adding a custom exercise is a deliberate button, not something that appears
 * under the search box once you've typed enough. Search-that-becomes-create
 * puts a "create" control exactly where a mistyped search lands, which is how
 * you end up owning "Bnech Press" — and it hides the feature from anyone who
 * hasn't already typed a name that matches nothing.
 */

import { useMemo, useState } from 'react'

import type { Exercise } from '../db'
import { api } from '../api/client'
import { db } from '../db'

const smallButton = {
  marginTop: 0,
  padding: '.25rem .6rem',
  minHeight: 0,
  fontSize: '.85rem',
} as const

export function ExercisePicker({
  exercises,
  onPick,
  onCancel,
}: {
  exercises: Exercise[]
  onPick: (id: number) => void
  onCancel?: () => void
}) {
  const [query, setQuery] = useState('')
  const [adding, setAdding] = useState(false)
  const [customName, setCustomName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<string | null>(null)

  const q = query.trim().toLowerCase()

  // Archived exercises stay in the cache so history can still name them, and
  // stay out of here so they stop being offered.
  const available = useMemo(
    () => exercises.filter((e) => !e.archived_at),
    [exercises],
  )

  const matches = useMemo(() => {
    if (!q) {
      // No query: show the compounds first. Somebody opening this mid-workout
      // is far more likely to want Squat than Russian Twist.
      const priority = ['barbell', 'dumbbell']
      return [...available]
        .sort((a, b) => {
          const pa = priority.indexOf(a.equipment)
          const pb = priority.indexOf(b.equipment)
          if (pa !== pb) return (pa < 0 ? 99 : pa) - (pb < 0 ? 99 : pb)
          return a.name.localeCompare(b.name)
        })
        .slice(0, 40)
    }
    return available
      .filter((e) => e.name.toLowerCase().includes(q) || e.slug.includes(q))
      .slice(0, 40)
  }, [available, q])

  const create = async () => {
    const name = customName.trim()
    if (!name) return

    // Already there — say so instead of creating a second one. Two exercises
    // with the same name split every chart and every PR between them.
    const existing = available.find((e) => e.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      setError(`You already have “${existing.name}” — it's in the list below.`)
      setQuery(name)
      setAdding(false)
      setCustomName('')
      return
    }

    setCreating(true)
    setError(null)
    try {
      // Creating an exercise needs the server (it owns the id space and the
      // alias table). This is the one action in the logging flow that requires
      // a connection — so say so plainly rather than failing with a generic
      // error.
      const res = await api<{ exercise: Exercise }>('/exercises', { body: { name } })
      await db.exercises.put(res.exercise)

      // Deliberately not selected for you. You asked to add it to the library,
      // which isn't the same as saying it's what you're about to do — and the
      // search is filled in so it's the first thing in the list either way.
      setCreated(res.exercise.name)
      setQuery(res.exercise.name)
      setAdding(false)
      setCustomName('')
    } catch {
      setError(
        "Couldn't create it — that needs a connection. Pick an existing " +
          'exercise for now and add this one later.',
      )
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="card">
      <div className="row">
        <strong>Pick an exercise</strong>
        <span style={{ display: 'flex', gap: '.4rem' }}>
          <button
            className="secondary"
            style={smallButton}
            onClick={() => {
              setAdding((was) => !was)
              setError(null)
              setCreated(null)
            }}
          >
            {adding ? 'Back to list' : 'Add custom exercise'}
          </button>
          {onCancel && (
            <button className="secondary" style={smallButton} onClick={onCancel}>
              Cancel
            </button>
          )}
        </span>
      </div>

      {adding && (
        <div
          style={{
            marginTop: '.6rem',
            padding: '.6rem .7rem',
            background: 'var(--surface-2)',
            borderRadius: 'var(--radius)',
          }}
        >
          <label htmlFor="custom-exercise" style={{ marginTop: 0 }}>
            Name it
          </label>
          <input
            id="custom-exercise"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Reverse Nordic Curl"
            autoComplete="off"
            autoCapitalize="words"
            onKeyDown={(e) => { if (e.key === 'Enter') void create() }}
          />
          <p className="muted" style={{ fontSize: '.8rem', margin: '.4rem 0 0' }}>
            Once it's created, check the list below for it — the search fills in
            with the name. It joins your exercise library, so it's there for
            every future workout and plan too.
          </p>

          {error && <div className="error" style={{ marginTop: '.5rem' }}>{error}</div>}

          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.5rem' }}>
            <button
              className="secondary"
              style={{ flex: 1 }}
              onClick={() => {
                setAdding(false)
                setCustomName('')
                setError(null)
              }}
            >
              Cancel
            </button>
            <button
              style={{ flex: 2 }}
              disabled={creating || customName.trim().length === 0}
              onClick={create}
            >
              {creating ? 'Creating…' : 'Create exercise'}
            </button>
          </div>
        </div>
      )}

      <label htmlFor="exercise-search">Search</label>
      <input
        id="exercise-search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setCreated(null)
        }}
        placeholder="bench, squat, row…"
        autoComplete="off"
        autoCapitalize="none"
      />

      {created && (
        <div className="muted" style={{ fontSize: '.85rem', marginTop: '.4rem' }}>
          Added <strong>{created}</strong> — it's in the list below.
        </div>
      )}

      {!adding && error && <div className="error">{error}</div>}

      <ul className="plain" style={{ maxHeight: '20rem', overflowY: 'auto' }}>
        {matches.map((e) => (
          <li
            key={e.id}
            className="tappable"
            onClick={() => onPick(e.id)}
            // Generous vertical padding: this list is scrolled and tapped
            // mid-workout, and the rows were previously only as tall as their
            // text.
            style={{ padding: '.7rem .5rem' }}
          >
            <span>{e.name}</span>
            <span className="muted">{e.equipment}</span>
          </li>
        ))}
        {matches.length === 0 && (
          <li className="muted">
            No matches. Use <strong>Add custom exercise</strong> above if it isn't
            in the library.
          </li>
        )}
      </ul>
    </div>
  )
}
